<template>
  <base-layout>
    <h1 slot="header">
      {{ `app.pages.settings.profile` | trans }}
    </h1>
    <a slot="buttons" :href="routes.settings" class="btn btn-primary">
      <i class="fa fa-arrow-circle-left" aria-hidden="true"></i>
      {{ `app.back`|trans }}
    </a>
    <v-app id="vuetify-app">
      <v-container fluid>
        <TestSubscriptionExtended/>
      </v-container>
      <v-container fluid>
        <v-layout align-center column>
          <div class="avatar-preview">
            <div id="imagePreview" v-bind:style="{'background-image':`url(${getImageUrl()})`}">
            </div>
          </div>
          <div>
            <button class="btn btn-success" @click="changeAvatar()">{{ `app.settings.avatar_change`|trans }}
            </button>
            <button v-if="profile.image" @click="removeAvatar()" class="btn btn-danger">
              {{ `app.delete`|trans }}
            </button>
          </div>
          <profile-image-upload
              @crop-upload-success="cropUploadSuccess"
              v-model="show"
              :field="`profile_image`"
              :langType="`pl`"
              :langExt="langExt"
              :width="300"
              :noSquare="true"
              :height="300"
              :url="routes.settings_profile_image"
              :method="`POST`"
              :headers="getHeaders()"
              img-format="png"></profile-image-upload>
        </v-layout>
      </v-container>
      <v-divider></v-divider>
      <v-container fluid>
        <h4>{{ `app.settings.user_data`|trans }}</h4>
        <v-form v-model="profileValid" ref="profile">
          <v-text-field
              :success="true"
              v-model="profile.name"
              :label="`app.first_name`|trans"
              :rules="rules.name"
              solo
          ></v-text-field>
          <v-text-field
              :success="true"
              v-model="profile.surname"
              :label="`app.surname`|trans"
              :rules="rules.surname"
              solo
          ></v-text-field>
        </v-form>
        <loading-button class="pull-right" :loaging="profileUpdateIsLoading" :status="profileUpdateStatus"
                        @click="updateProfile()">
          <i class="fa fa-save"></i> {{ `app.save`|trans }}
        </loading-button>
      </v-container>
      <v-divider></v-divider>
      <v-container fluid>
        <h4>{{ `app.password`|trans }}</h4>
        <v-alert :value="true" :type="`info`" class="mt-4 mb-4">
          {{ `app.settings.leave_empty_password` | trans }}
        </v-alert>
        <v-form ref="password" v-model="passwordValid">
          <v-text-field
              v-model="password"
              :type="`password`"
              :label="`app.password` | trans"
              :rules="rules.password"
              solo
          ></v-text-field>
          <v-text-field
              v-model="password_confirmation"
              :type="`password`"
              :label="`app.password_confirmation` | trans"
              @input="passwordConfirmationOnInput"
              solo
          ></v-text-field>
        </v-form>
        <loading-button class="pull-right" :loaging="passwordChangeIsLoading" :status="passwordChangeStatus"
                        @click="changePassword()">
          <i class="fa fa-save"></i> {{ `app.save`|trans }}
        </loading-button>
      </v-container>
    </v-app>
  </base-layout>
</template>

<script>
import {mapActions} from "vuex";
import profileImageUpload from 'vue-image-crop-upload'
import TestSubscriptionExtended from "./TestSubscriptionExtended";
import {trans} from "../../app";

export default {
  props: {
    routes: Object
  },
  components: {
    'profile-image-upload': profileImageUpload,
    'TestSubscriptionExtended': TestSubscriptionExtended
  },
  data: () => ({
    profileImageDataUrl: '',
    profileValid: false,
    passwordValid: false,
    profileUpdateIsLoading: false,
    passwordChangeIsLoading: false,
    profileUpdateStatus: '',
    passwordChangeStatus: '',
    profile: {
      name: '',
      surname: '',
    },
    show: false,
    langExt: {
      hint: trans('app.settings.drag_and_drop'),
      loading: trans('app.uploading'),
      success: trans('app.settings.avatar_change_success'),
      fail: trans('app.settings.avatar_change_failure'),
      preview: trans('app.preview'),
      btn: {
        off: trans('app.cancel'),
        close: trans('app.close'),
        back: trans('app.back'),
        save: trans('app.save'),
      },
      error: {
        onlyImg: trans('app.settings.wrong_avatar_extension'),
        lowestPx: trans('app.settings.wrong_avatar_resolution'),
      }
    },
    password: '',
    password_confirmation: '',
  }),
  computed: {
    rules() {
      return {
        name: [
          v => !!v || trans('app.settings.validation.profile.name.required')
        ],
        surname: [
          v => !!v || trans('app.settings.validation.profile.surname.required')
        ],
        password: [
          v => !!v || trans('app.settings.validation.password.required'),
          v => v && v.length >= 8 || trans('app.settings.validation.password.min', {min: '8'}),
          v => v && v.length <= 100 || trans('app.settings.validation.password.max', {max: '100'}),
          v => v === this.password_confirmation || trans('app.settings.validation.password.confirmed'),
        ],
      }
    },
  },
  methods: {
    ...mapActions(['fetchSettings']),
    async updateProfile() {
      this.$refs.profile.validate();

      if (!this.profileValid) {
        this.profileUpdateStatus = false;
        return;
      }

      this.profileUpdateIsLoading = true;

      try {
        await axios.put('/settings/profile', this.profile);
        this.profileUpdateStatus = true;
        this.$toast.success(trans('app.actions.saved', {name: trans('app.pages.settings.index').toLowerCase()}));
        setTimeout(() => {
          window.location.reload(true);
        }, 500);
      } catch (error) {
        this.profileUpdateStatus = false;
        if (error.response && error.response.status === 422) {
          this.$toast.error(trans('app.save_failed'));
        } else {
          this.sentryCaptureException(error);
        }
      }
      this.profileUpdateIsLoading = false;
    },

    async changePassword() {
      this.$refs.password.validate();

      if (!this.passwordValid) {
        this.passwordChangeStatus = false;
        return;
      }

      this.passwordChangeIsLoading = true;

      try {
        await axios.post(this.routes.profile_update_password, {
          password: this.password,
          password_confirmation: this.password_confirmation
        });
        this.$refs.password.reset();
        this.passwordChangeStatus = true;
        this.$toast.success(trans('app.settings.password_changed'));
        this.password = '';
        this.password_confirmation = '';
      } catch (error) {
        this.passwordChangeStatus = false;
        if (error.response && error.response.status === 422) {
          this.$toast.error(trans('app.save_failed'));
        } else {
          this.sentryCaptureException(error);
        }
      }

      this.passwordChangeIsLoading = false;
    },

    passwordConfirmationOnInput() {
      this.$refs.password.validate();
    },

    getImageUrl() {
      return this.profile.image ? `/photos/avatars/${this.profile.image}` : '/img/noprofile.png';
    },

    getHeaders() {
      return {
        'X-CSRF-TOKEN': document.head.querySelector('meta[name="csrf-token"]').content,
      }
    },

    changeAvatar() {
      this.show = !this.show;
    },

    removeAvatar() {
      this.$dialog.confirm(trans('app.settings.avatar_change_confirm')).then(async () => {
        try {
          await axios.delete(`/settings/profile/image/${this.profile.image}`);
          this.profile.image = null;
          await axios.put('/settings/profile', this.profile);
          setTimeout(() => {
            window.location.reload(true);
          }, 500);
        } catch (error) {
          if (error.response && error.response.status === 422) {
            this.$toast.error(trans('app.save_failed'));
          } else {
            this.sentryCaptureException(error);
          }
        }
      }).catch(() => {});
    },

    async cropUploadSuccess(response) {
      this.profile.image = response.image;
      await axios.put('/settings/profile', this.profile);
      setTimeout(() => {
        window.location.reload(true);
      }, 500);
    },
  },
  created() {
    this.fetchSettings()
        .then(() => {
          this.profile = this.$store.state.settings.profile;
        }).catch((error) => {
          this.sentryCaptureException(error);
        });
  }
}
</script>
<style scoped>
.avatar-preview {
  width: 192px;
  height: 192px;
  position: relative;
  border-radius: 100%;
  border: 6px solid #F8F8F8;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.avatar-preview > div {
  width: 100%;
  height: 100%;
  border-radius: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}
</style>
